/**
 * THE ENGAGEMENT FILE (ADR-0021 §5b) — what Leveri knows about this client and
 * engagement before any transaction is worked, layer by layer: the engagement,
 * the client, the opening position, the business, its records, its policies.
 *
 * What is known reads as a line of the file: the answer, who stated it, who
 * recorded it. What is not known reads as the question, with who can answer it
 * and why it matters — and one answer form opens at a time, under the question
 * it answers. Every word, every count and every decision about who may set a
 * policy is the server's; the browser renders and sends.
 */
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { EngagementFileResponse, type Engagement, type FileEntry } from "@leveri/contracts";
import { Button, Citation, Field, Notice, Reading, RuledList, Section, SectionHead, StateText, TextField } from "../file/index.js";
import { call, failureMessage } from "../api.js";
import { usePlaceWords } from "./shell.js";

const STATUS_WORDS: Record<FileEntry["status"], string> = {
  ESTABLISHED: "Established", ASSUMED: "Assumed until confirmed", NOT_APPLICABLE: "Does not apply", UNKNOWN: "Not known",
};
const toneOf = (e: FileEntry) =>
  e.status === "ESTABLISHED" ? "established" as const
    : e.status === "ASSUMED" ? "attention" as const
      : e.status === "UNKNOWN" && e.needed ? "attention" as const
        : "optional" as const;

export function EngagementFileSection({ engagement }: { engagement: Engagement }) {
  const [file, setFile] = useState<EngagementFileResponse | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [answering, setAnswering] = useState<string | null>(null);
  const place = usePlaceWords();

  const load = useCallback(async () => {
    try {
      setFile(await call("GET", `/api/engagements/${engagement.id}/file`, EngagementFileResponse));
    } catch (error) {
      setFailure(failureMessage(error));
    }
  }, [engagement.id]);
  useEffect(() => { void load(); }, [load]);

  return (
    <Section>
      <SectionHead>What Leveri knows about this {place.engagement}</SectionHead>
      {failure && <Notice>{failure}</Notice>}
      {!file && !failure && <Reading>Reading what Leveri knows…</Reading>}
      {file && (
        <>
          <p className="text-[15px] text-ink" data-testid="file-summary">{file.summary}</p>
          {file.layers.filter((layer) => layer.entries.length > 0).map((layer) => (
            <div key={layer.layer} className="mt-6" data-testid={`file-layer-${layer.layer}`}>
              <div className="mb-1 text-[13px] font-medium text-stone">{layer.title}</div>
              <RuledList>
                {layer.entries.map((entry) => (
                  <EntryRow
                    key={entry.key}
                    entry={entry}
                    engagement={engagement}
                    open={answering === entry.key}
                    mayAnswer={entry.shape !== null && (!entry.policy || file.viewerMaySetPolicy)}
                    onOpen={() => setAnswering(entry.key)}
                    onClose={() => setAnswering(null)}
                    onRecorded={(next) => { setFile(next); setAnswering(null); }}
                  />
                ))}
              </RuledList>
            </div>
          ))}
        </>
      )}
    </Section>
  );
}

interface EntryRowProps {
  entry: FileEntry;
  engagement: Engagement;
  open: boolean;
  mayAnswer: boolean;
  onOpen: () => void;
  onClose: () => void;
  onRecorded: (file: EngagementFileResponse) => void;
}

function EntryRow({ entry, engagement, open, mayAnswer, onOpen, onClose, onRecorded }: EntryRowProps) {
  const unknown = entry.status === "UNKNOWN";
  const recorded = [
    entry.statedBy === "CLIENT" ? "Stated by the client" : null,
    entry.recordedByName ? `recorded by ${entry.recordedByName}` : null,
    entry.recordedInTitle ? `in ${entry.recordedInTitle}` : null,
  ].filter(Boolean).join(", ");
  return (
    <div className="grid grid-cols-1 gap-x-6 gap-y-1 py-3 md:grid-cols-[170px_minmax(0,1fr)]" data-testid={`file-entry-${entry.key}`}>
      <div className="text-[15px] text-stone">{entry.label}</div>
      <div className="min-w-0">
        {unknown ? (
          <p className="text-[15px] text-ink">{entry.question}</p>
        ) : (
          <p className="text-[15px] text-ink">{entry.valueText}</p>
        )}
        {entry.items.length > 0 && (
          <ul className="mt-1 list-disc pl-5 text-[13px] text-ink-soft">
            {entry.items.map((item) => <li key={item}>{item}</li>)}
          </ul>
        )}
        {entry.clause && <p className="mt-1 text-[13px] text-ink-soft">Policy: {entry.clause}</p>}
        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-stone">
          {entry.origin === "RECORDED" || unknown ? <StateText tone={toneOf(entry)}>{STATUS_WORDS[entry.status]}</StateText> : null}
          {unknown && <span>{entry.answeredBy === "CLIENT" ? "The client can answer" : "Yours to decide"}{entry.needed ? "" : " · optional"}</span>}
          {recorded && <span>{recorded}</span>}
          {entry.recordedAt && <Citation>{entry.recordedAt.slice(0, 10)}</Citation>}
          {mayAnswer && !open && !unknown && (
            <Button variant="ghost" size="sm" className="h-auto px-0" onClick={onOpen} data-testid={`file-answer-${entry.key}`}>Change</Button>
          )}
        </div>
        {entry.basis && entry.origin === "RECORDED" && <p className="mt-1 text-[13px] text-stone">{entry.basis}</p>}
        {(unknown || open) && <p className="mt-1 text-[13px] text-stone">{entry.why}</p>}
        {entry.feeds && (unknown || open) && <p className="mt-1 text-[13px] text-stone">{entry.feeds}</p>}
        {mayAnswer && !open && unknown && (
          <div className="mt-2">
            <Button variant="secondary" size="sm" onClick={onOpen} data-testid={`file-answer-${entry.key}`}>Answer</Button>
          </div>
        )}
        {open && <AnswerForm entry={entry} engagement={engagement} onClose={onClose} onRecorded={onRecorded} />}
      </div>
    </div>
  );
}

function AnswerForm({ entry, engagement, onClose, onRecorded }: { entry: FileEntry; engagement: Engagement; onClose: () => void; onRecorded: (file: EngagementFileResponse) => void }) {
  const [status, setStatus] = useState<"ESTABLISHED" | "ASSUMED" | "NOT_APPLICABLE">("ESTABLISHED");
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const shape = entry.shape!;
  const reasonRequired = entry.policy || status !== "ESTABLISHED";

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (saving) return;
    const form = new FormData(event.currentTarget);
    const text = (name: string) => String(form.get(name) ?? "").trim();
    const body: Record<string, unknown> = { key: entry.key, status, basis: text("basis") || undefined };
    if (!entry.policy) body["statedBy"] = text("statedBy") || "ACCOUNTANT";
    if (status !== "NOT_APPLICABLE") {
      if (shape.kind === "CHOICE") body["value"] = text("value") || null;
      if (shape.kind === "CHOICES") body["value"] = form.getAll("value").map(String);
      if (shape.kind === "YES_NO") body["value"] = text("value") === "yes" ? true : text("value") === "no" ? false : null;
      if (shape.kind === "TEXT") body["value"] = text("value");
      if (shape.kind === "AMOUNT") body["amount"] = text("amount");
    }
    setSaving(true);
    setFailure(null);
    try {
      onRecorded(await call("POST", `/api/engagements/${engagement.id}/file`, EngagementFileResponse, body));
    } catch (error) {
      setFailure(failureMessage(error));
    } finally {
      setSaving(false);
    }
  }

  const option = (type: "radio" | "checkbox", value: string, label: string) => (
    <label key={value} className="grid cursor-pointer grid-cols-[18px_minmax(0,1fr)] items-baseline gap-3 py-1.5">
      <input type={type} name="value" value={value} className="justify-self-center accent-forest" disabled={saving} data-testid={`file-option-${value}`} />
      <span className="text-[15px] text-ink">{label}</span>
    </label>
  );

  return (
    <form onSubmit={submit} className="mt-3 space-y-4" data-testid={`file-form-${entry.key}`}>
      <fieldset className="space-y-3" disabled={saving}>
        {status !== "NOT_APPLICABLE" && (
          <div>
            {shape.kind === "CHOICE" && shape.options.map((o) => option("radio", o.value, o.label))}
            {shape.kind === "CHOICES" && shape.options.map((o) => option("checkbox", o.value, o.label))}
            {shape.kind === "YES_NO" && [option("radio", "yes", "Yes"), option("radio", "no", "No")]}
            {shape.kind === "TEXT" && (
              <textarea name="value" rows={2} maxLength={shape.max} required
                className="w-full rounded-control border border-line-strong bg-canvas px-3 py-2 text-sm text-ink focus:border-brand" data-testid="file-text" />
            )}
            {shape.kind === "AMOUNT" && (
              <TextField name="amount" label={`Amount (${engagement.currency})`} inputMode="decimal" required data-testid="file-amount" />
            )}
          </div>
        )}
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-[13px] text-ink-soft">
          {(["ESTABLISHED", "ASSUMED", "NOT_APPLICABLE"] as const).map((s) => (
            <label key={s} className="inline-flex cursor-pointer items-center gap-2">
              <input type="radio" name="status" className="accent-forest" checked={status === s} onChange={() => setStatus(s)} data-testid={`file-status-${s}`} />
              {s === "ESTABLISHED" ? "Known" : s === "ASSUMED" ? "Assume it for now" : "Does not apply"}
            </label>
          ))}
        </div>
        {!entry.policy && (
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-[13px] text-ink-soft">
            {(["CLIENT", "ACCOUNTANT"] as const).map((who) => (
              <label key={who} className="inline-flex cursor-pointer items-center gap-2">
                <input type="radio" name="statedBy" value={who} className="accent-forest" defaultChecked={entry.answeredBy === who} />
                {who === "CLIENT" ? "The client told us" : "From my own knowledge"}
              </label>
            ))}
          </div>
        )}
        <Field
          label={reasonRequired ? "Reason" : "Note"}
          hint={entry.policy ? "Filed with the policy; the accounts cite it." : status === "ASSUMED" ? "Why the work may proceed on it." : status === "NOT_APPLICABLE" ? "Why it does not apply." : "Optional: where this came from."}
        >
          <textarea name="basis" rows={2} required={reasonRequired}
            className="w-full rounded-control border border-line-strong bg-canvas px-3 py-2 text-sm text-ink focus:border-brand" data-testid="file-basis" />
        </Field>
      </fieldset>
      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" disabled={saving} data-testid="file-record">Record</Button>
        <Button type="button" variant="ghost" disabled={saving} onClick={onClose}>Cancel</Button>
      </div>
      {saving && <Reading>Recording…</Reading>}
      {failure && <Notice>{failure}</Notice>}
    </form>
  );
}
